/**
 * Technology Domain Detail Route
 *
 * Displays comprehensive technology domain metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useTechnologyDomain } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteTechnologyDomain, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './technologyDomainId.css';

export const Route = createFileRoute('/technology-domains/$technologyDomainId')({
  component: TechnologyDomainDetailPage,
});

function TechnologyDomainDetailPage(): JSX.Element {
  const { technologyDomainId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch technology domain data
  const { data, loading, error: fetchError } = useTechnologyDomain(technologyDomainId);
  const technologyDomain = data?.technologyDomain;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="technology-domain-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="technology-domain-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load technology domain"
          description={fetchError.message}
          actionLabel="Back to Technology Domains"
          onAction={() =>
            navigate({ to: '/technology-domains', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Not found state
  if (!technologyDomain) {
    return (
      <div className="technology-domain-detail-page">
        <EmptyState
          icon="search_off"
          title="Technology domain not found"
          description={`No technology domain found with ID: ${technologyDomainId}`}
          actionLabel="Back to Technology Domains"
          onAction={() =>
            navigate({ to: '/technology-domains', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({ to: `/technology-domains/${technologyDomain.entityId}/edit` as string });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteTechnologyDomain(technologyDomainId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListTechnologyDomains'],
      });

      // Navigate back to technology domains list
      navigate({ to: '/technology-domains', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="technology-domain-detail-page">
      {/* Header */}
      <div className="technology-domain-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() =>
              navigate({ to: '/technology-domains', search: { page: 1, pageSize: 25 } })
            }
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Technology Domains
          </ForgeButton>
          <h1 className="forge-typography--headline4">{technologyDomain.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {technologyDomain.description || 'No description provided'}
          </p>
        </div>
        <div className="technology-domain-detail-actions">
          <PermissionGate permission="technology-domain:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="technology-domain:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="technology-domain-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Technology Domain ID</span>
              <span className="detail-value">{technologyDomain.entityId}</span>
            </div>
          </div>
        </CardElement>

        {/* Additional Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Additional Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span className="detail-value">
                {technologyDomain.tags && technologyDomain.tags.length > 0
                  ? technologyDomain.tags.join(', ')
                  : '—'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Notes</span>
              <span className="detail-value">{technologyDomain.notes || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(technologyDomain._createdAt)} by {technologyDomain._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(technologyDomain._updatedAt)} by {technologyDomain._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Technology Domain"
          message="Are you sure you want to delete this technology domain?"
          warning="This action will soft delete the technology domain. It can be recovered by an administrator."
          itemName={technologyDomain.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
